import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Navigate, Link } from 'react-router-dom';
import { Loader, Center, Button } from '@mantine/core';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { getReportBundle } from '../services/reportService';
import { getRubric, tabulate, getRankBadgeTopN, earnsRankBadge } from '../config/rubrics';
import { CONTACT_EMAIL } from '../config/seasons';
import pdcLogo from '../assets/prix_du_canada_main_logo.png';

// ---------------------------------------------------------------------------
// /report/:registrationId
//
// One page per registration, one report block per variation (a dancer with
// two variations gets two blocks, page-broken for print). Data comes from
// `judging_reports` (one row per variation, scores + feedback as JSON) and the
// rubric for that competition year in src/config/rubrics.js — so the page
// renders N categories / N feedback sections with nothing hardcoded.
//
// RLS only returns PUBLISHED report rows to the owning user, so "no rows"
// means "not released yet", never "you can't see this".
// ---------------------------------------------------------------------------

// Ordinal helper — 1 → "1st", 2 → "2nd", 10 → "10th", etc.
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const SERIF = "'Playfair Display', 'Georgia', serif";

const cardStyle = {
  background: 'white',
  borderRadius: '10px',
  border: '1px solid rgba(0,0,0,0.08)',
  padding: '2rem',
  marginBottom: '1.5rem',
};

const eyebrowStyle = {
  fontSize: '0.75rem',
  color: 'var(--primary-gold)',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginBottom: '0.35rem',
};

// ---------- Radar chart (N axes) with hover tooltip ------------------------

const RadarChart = ({ rows, rubric }) => {
  const [hovered, setHovered] = useState(null);
  const size = 340;
  const cx = size / 2;
  const cy = size / 2;
  const rMax = 120;
  const n = rows.length;
  const ringCount = 5;
  const rings = Array.from({ length: ringCount }, (_, i) => ((i + 1) / ringCount) * rubric.scaleMax);

  // Axes evenly spaced starting at 12 o'clock. For n = 3 this is exactly
  // -90°, 30°, 150° — the original layout.
  const axes = rows.map((row, i) => {
    const rad = ((-90 + (i * 360) / n) * Math.PI) / 180;
    return { row, ux: Math.cos(rad), uy: Math.sin(rad) };
  });

  const pointFor = (axis, value) => {
    const r = (value / rubric.scaleMax) * rMax;
    return [cx + axis.ux * r, cy + axis.uy * r];
  };

  const polyPoints = axes.map((axis) => pointFor(axis, axis.row.average).join(',')).join(' ');

  // Extra horizontal room for side labels when n > 3 (labels then sit left /
  // right of the vertex instead of above / below).
  const padX = n > 3 ? 60 : 0;

  return (
    <div style={{ position: 'relative' }}>
      <svg
        className="report-radar-svg"
        viewBox={`${-padX} -18 ${size + padX * 2} ${size + 38}`}
        style={{ width: '100%', maxWidth: `${340 + padX}px`, height: 'auto', display: 'block', margin: '0 auto' }}
        role="img"
        aria-label="Category averages radar chart"
      >
        {rings.map((v) => (
          <polygon
            key={v}
            points={axes.map((axis) => pointFor(axis, v).join(',')).join(' ')}
            fill="none"
            stroke="rgba(0,0,0,0.08)"
            strokeWidth="1"
          />
        ))}
        {axes.map((axis, i) => {
          const [x, y] = pointFor(axis, rubric.scaleMax);
          return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(0,0,0,0.12)" strokeWidth="1" />;
        })}
        <polygon
          points={polyPoints}
          fill="var(--primary-gold)"
          fillOpacity="0.22"
          stroke="var(--primary-gold)"
          strokeWidth="2.5"
        />
        {axes.map((axis, i) => {
          const [x, y] = pointFor(axis, axis.row.average);
          const isHovered = hovered === i;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={isHovered ? 8 : 5}
              fill="var(--deep-gold)"
              stroke="white"
              strokeWidth={isHovered ? 2 : 0}
              style={{ cursor: 'pointer', transition: 'r 0.15s ease' }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}
        {axes.map((axis, i) => {
          // Label placement relative to the outer vertex:
          //   top vertex → above; bottom vertices → below; anything else → beside.
          const [vx, vy] = pointFor(axis, rubric.scaleMax);
          let lx = vx;
          let ly = vy;
          let anchor = 'middle';
          if (axis.uy <= -0.5) {
            ly = vy - 22;
          } else if (axis.uy >= 0.45) {
            ly = vy + 28;
          } else {
            lx = vx + (axis.ux > 0 ? 12 : -12);
            ly = vy + 5;
            anchor = axis.ux > 0 ? 'start' : 'end';
          }
          const label = axis.row.cat.shortLabel || axis.row.cat.label;
          return (
            <g key={`lbl-${i}`}>
              <text x={lx} y={ly} textAnchor={anchor} fontSize="13" fontWeight="600" fill="var(--charcoal)">
                {label}
              </text>
              <text x={lx} y={ly + 16} textAnchor={anchor} fontSize="13" fontWeight="700" fill="var(--primary-gold)">
                {axis.row.average.toFixed(1)} / {rubric.scaleMax}
              </text>
            </g>
          );
        })}
      </svg>

      {hovered !== null && rows[hovered].cat.description && (
        <div
          className="report-no-print"
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'var(--charcoal)',
            color: 'white',
            padding: '0.7rem 0.9rem',
            borderRadius: '6px',
            fontSize: '0.78rem',
            lineHeight: 1.55,
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
            maxWidth: '240px',
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.82rem' }}>
            {rows[hovered].cat.label}
          </div>
          <div style={{ opacity: 0.9 }}>{rows[hovered].cat.description}</div>
        </div>
      )}
    </div>
  );
};

// ---------- Rank badge (medallion style) ----------------------------------

const RankBadge = ({ rank, topN }) => (
  <div style={{
    position: 'relative',
    padding: '0.7rem 1.5rem',
    background: 'linear-gradient(135deg, #f4c542 0%, var(--primary-gold) 45%, var(--deep-gold) 100%)',
    color: 'white',
    borderRadius: '10px',
    boxShadow: '0 6px 18px rgba(184, 134, 11, 0.32), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -1px 0 rgba(0,0,0,0.08)',
    textAlign: 'center',
    minWidth: '130px',
    fontFamily: SERIF,
    border: '2px solid rgba(255,255,255,0.5)',
  }}>
    <div style={{
      position: 'absolute',
      top: '2px',
      left: '8px',
      right: '8px',
      height: '35%',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0))',
      borderRadius: '8px 8px 40% 40%',
      pointerEvents: 'none',
    }} />
    <div style={{
      fontSize: '0.7rem',
      fontWeight: 800,
      letterSpacing: '0.2em',
      opacity: 0.95,
      marginBottom: '0.15rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.35rem',
    }}>
      <span style={{ fontSize: '0.95rem' }}>★</span> {topN === 1 ? 'WINNER' : `TOP ${topN}`} <span style={{ fontSize: '0.95rem' }}>★</span>
    </div>
    <div style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em' }}>
      {ordinal(rank)}
    </div>
    <div style={{
      fontSize: '0.65rem',
      fontWeight: 700,
      letterSpacing: '0.18em',
      opacity: 0.9,
      marginTop: '0.15rem',
      textTransform: 'uppercase',
      fontFamily: 'system-ui, sans-serif',
    }}>
      Place
    </div>
  </div>
);

// ---------- One variation's report ----------------------------------------

const VariationReport = ({ dancer, variation, report, rubric, year, isFirst }) => {
  const t = tabulate(report.scores, rubric);
  const rank = report.rank;
  const showRank = earnsRankBadge(rubric, report);
  const badgeTopN = getRankBadgeTopN(rubric, report.rank_group_size);
  const feedback = report.feedback || {};
  const feedbackSections = rubric.feedbackSections.filter((s) => feedback[s.key]);

  return (
    <div id={`v-${variation.id}`} className={isFirst ? '' : 'report-variation-break'} style={{ scrollMarginTop: '90px' }}>
      {/* Competitor card — logo + title letterhead, then dancer + variation + score */}
      <section className="report-card" style={{ ...cardStyle, borderTop: '4px solid var(--primary-gold)', padding: '1.75rem 2rem' }}>
        <div className="report-letterhead report-header-print">
          <img
            src={pdcLogo}
            alt="Prix du Canada"
            className="report-logo"
            style={{ height: '150px', width: 'auto', display: 'block' }}
          />
          <div className="report-letterhead-title">
            Adjudication Report · {year}
          </div>
        </div>

        <div className="report-competitor-grid">
          <div>
            <div style={eyebrowStyle}>Competitor</div>
            <h1 className="report-h1" style={{ fontFamily: SERIF, fontSize: '2rem', margin: '0 0 0.25rem 0', color: 'var(--charcoal)' }}>
              {dancer.first_name} {dancer.last_name}
            </h1>
            <div className="report-body" style={{ color: 'var(--soft-gray)', fontSize: '0.95rem' }}>
              {[variation.age_division, variation.performance_category].filter(Boolean).join(' · ')}
            </div>
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px dashed rgba(0,0,0,0.08)' }}>
              <div style={eyebrowStyle}>Variation</div>
              <div style={{ color: 'var(--charcoal)', fontSize: '1.05rem', fontWeight: 600 }}>
                {variation.variation_name}
              </div>
              <div className="report-body" style={{ color: 'var(--soft-gray)', fontSize: '0.9rem' }}>
                {variation.ballet_origin && <>from <em>{variation.ballet_origin}</em></>}
                {variation.character_name ? ` · ${variation.character_name}` : ''}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.9rem', minWidth: '160px' }}>
            {showRank && <RankBadge rank={rank} topN={badgeTopN} />}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--soft-gray)', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, marginBottom: '0.35rem' }}>
                Final Score
              </div>
              <div className="report-final-score" style={{ fontFamily: SERIF, fontSize: '3.4rem', lineHeight: 1, color: 'var(--primary-gold)', fontWeight: 700 }}>
                {t.totalFinal}
              </div>
              <div style={{ color: 'var(--soft-gray)', fontSize: '0.95rem', marginTop: '0.2rem' }}>
                / {t.totalMax}
              </div>
              {t.overallPct >= (rubric.hideBandBelowPct ?? 0) && (
                <div style={{
                  display: 'inline-block',
                  marginTop: '0.6rem',
                  padding: '3px 12px',
                  background: t.band.color,
                  color: 'white',
                  borderRadius: '999px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                }}>
                  {t.band.label} · {t.overallPct.toFixed(0)}%
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Score breakdown — radar + scorecard */}
      <section className="report-card" style={cardStyle}>
        <h2 className="report-h2" style={{ fontFamily: SERIF, fontSize: '1.15rem', margin: '0 0 0.35rem 0', color: 'var(--charcoal)', textAlign: 'center' }}>
          Score Breakdown
        </h2>
        <p className="report-body report-no-print" style={{ color: 'var(--soft-gray)', fontSize: '0.85rem', margin: '0 0 1.5rem 0', textAlign: 'center' }}>
          Hover a point on the radar to learn what each category measures.
        </p>

        <div className="report-viz-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem',
          alignItems: 'center',
        }}>
          <div>
            <RadarChart rows={t.rows} rubric={rubric} />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--primary-gold)' }}>
                  <th style={{ textAlign: 'left',   padding: '0.6rem 0.5rem', color: 'var(--charcoal)', fontWeight: 700 }}>Category</th>
                  <th style={{ textAlign: 'center', padding: '0.6rem 0.5rem', color: 'var(--charcoal)', fontWeight: 700 }}>Total</th>
                  <th style={{ textAlign: 'center', padding: '0.6rem 0.5rem', color: 'var(--charcoal)', fontWeight: 700 }}>Final</th>
                </tr>
              </thead>
              <tbody>
                {t.rows.map((r) => (
                  <tr key={r.cat.key} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <td style={{ padding: '0.7rem 0.5rem', color: 'var(--charcoal)' }}>
                      {r.cat.label}
                      {r.cat.weight !== 1 && (
                        <div style={{ color: 'var(--primary-gold)', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em' }}>
                          ×{r.cat.weight} AT TABULATION
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center', padding: '0.7rem 0.5rem', color: 'var(--charcoal)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {r.combined}<span style={{ color: 'var(--soft-gray)', fontWeight: 400, fontSize: '0.85em' }}> / {r.combinedMax}</span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '0.7rem 0.5rem', color: 'var(--primary-gold)', fontWeight: 700, fontSize: '1.05rem' }}>
                      {r.final}
                      {r.cat.weight !== 1 && (
                        <div style={{ color: 'var(--soft-gray)', fontSize: '0.68rem', fontWeight: 500 }}>
                          {r.combined} × {r.cat.weight}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ padding: '0.9rem 0.5rem', fontWeight: 700, color: 'var(--charcoal)' }}>Total</td>
                  <td style={{ padding: '0.9rem 0.5rem' }}></td>
                  <td style={{ textAlign: 'center', padding: '0.9rem 0.5rem', fontFamily: SERIF, fontWeight: 700, fontSize: '1.35rem', color: 'var(--primary-gold)', whiteSpace: 'nowrap' }}>
                    {t.totalFinal} <span style={{ color: 'var(--soft-gray)', fontSize: '1rem', fontWeight: 400 }}>/ {t.totalMax}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Feedback — one block per rubric section that has text */}
      {feedbackSections.length > 0 && (
        <section className="report-card" style={cardStyle}>
          <h2 className="report-h2" style={{ fontFamily: SERIF, fontSize: '1.15rem', margin: '0 0 0.35rem 0', color: 'var(--charcoal)' }}>
            Judges' Feedback
          </h2>
          <p className="report-body" style={{ color: 'var(--soft-gray)', fontSize: '0.85rem', margin: '0 0 1.25rem 0' }}>
            Synthesized from the judges' notes.
          </p>

          {feedbackSections.map((s, i) => (
            <div key={s.key}>
              <h3 style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.14em', color: 'var(--charcoal)', textTransform: 'uppercase', margin: '0 0 0.6rem 0' }}>
                {s.heading}
              </h3>
              <p className="report-body" style={{ color: 'var(--charcoal)', fontSize: '0.95rem', lineHeight: 1.7, margin: i < feedbackSections.length - 1 ? '0 0 1.5rem 0' : 0, whiteSpace: 'pre-line' }}>
                {feedback[s.key]}
              </p>
            </div>
          ))}
        </section>
      )}

      {/* Score reference — compact legend strip */}
      {rubric.scoreReference?.length > 0 && (
        <section className="report-card report-scoreref" style={{ ...cardStyle, background: 'var(--soft-cream)', padding: '1rem 1.5rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--soft-gray)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            Score Reference
          </div>
          {rubric.scoreReference.map((s) => (
            <div key={s.range} className="report-scoreref-item" style={{ textAlign: 'center', fontSize: '0.82rem', lineHeight: 1.35 }}>
              <div style={{ fontWeight: 700, color: 'var(--primary-gold)' }}>{s.range}</div>
              <div style={{ color: 'var(--charcoal)', fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
};

// ---------- Page shells ----------------------------------------------------

const Shell = ({ title, children }) => (
  <Layout title={title} description="Individual judging report and scorecard from Prix du Canada.">
    <div className="report-page" style={{ background: 'white', paddingBottom: '4rem', paddingTop: '2.5rem' }}>
      <div className="container" style={{ maxWidth: '960px', padding: '0 1rem' }}>
        {children}
      </div>
    </div>
  </Layout>
);

const Notice = ({ emoji, heading, body, cta }) => (
  <section style={{ ...cardStyle, borderTop: '4px solid var(--primary-gold)', textAlign: 'center', padding: '3rem 1.5rem' }}>
    <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{emoji}</div>
    <h1 style={{ fontFamily: SERIF, fontSize: '1.5rem', color: 'var(--charcoal)', margin: '0 0 0.75rem 0' }}>{heading}</h1>
    <p style={{ color: 'var(--soft-gray)', fontSize: '0.95rem', lineHeight: 1.6, maxWidth: '480px', margin: '0 auto 1.5rem' }}>{body}</p>
    {cta}
  </section>
);

const BackToProfile = () => (
  <Button component={Link} to="/profile" variant="outline" style={{ borderColor: 'var(--primary-gold)', color: 'var(--primary-gold)' }}>
    Back to My Profile
  </Button>
);

// ---------- Page -----------------------------------------------------------

const Report = () => {
  const { registrationId } = useParams();
  const { hash } = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Deep link from a profile variation card: /report/:id#v-<variationId>.
  // Scroll once the blocks have rendered (only matters with 2+ variations).
  useEffect(() => {
    if (loading || !hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, hash]);

  useEffect(() => {
    let cancelled = false;
    if (authLoading || !user || !registrationId) return undefined;
    (async () => {
      try {
        setLoading(true);
        const data = await getReportBundle(registrationId);
        if (!cancelled) setBundle(data);
      } catch (err) {
        console.error('Report load error:', err);
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user, registrationId]);

  if (!authLoading && !user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(`/report/${registrationId}`)}`} replace />;
  }

  if (authLoading || loading) {
    return (
      <Shell title="Judging Report | Prix du Canada">
        <Center style={{ minHeight: '40vh' }}><Loader color="var(--primary-gold)" /></Center>
      </Shell>
    );
  }

  if (error || !bundle?.registration) {
    return (
      <Shell title="Judging Report | Prix du Canada">
        <Notice
          emoji="🔍"
          heading="Report not found"
          body="We couldn't find a registration at this address under your account. If you think this is a mistake, please get in touch."
          cta={<BackToProfile />}
        />
      </Shell>
    );
  }

  const { registration, dancerInfo, variations, reports } = bundle;
  const year = registration.competition_year;
  const rubric = getRubric(year);

  if (reports.length === 0) {
    return (
      <Shell title="Judging Report | Prix du Canada">
        <Notice
          emoji="⏳"
          heading={`Your ${year} report isn't ready yet`}
          body="Reports are released to all dancers at the same time once tabulation is complete. We'll let you know by email — check back on your profile."
          cta={<BackToProfile />}
        />
      </Shell>
    );
  }

  if (!rubric) {
    return (
      <Shell title="Judging Report | Prix du Canada">
        <Notice
          emoji="⚠️"
          heading="We're having trouble displaying this report"
          body={`The ${year} scoring rubric isn't available on the site yet. Please email ${CONTACT_EMAIL} and we'll sort it out.`}
          cta={<BackToProfile />}
        />
      </Shell>
    );
  }

  // Pair each published report with its variation, in variation order.
  const blocks = variations
    .map((v) => ({ variation: v, report: reports.find((r) => r.variation_id === v.id) }))
    .filter((b) => b.report);

  const dancer = dancerInfo || { first_name: '', last_name: '' };

  return (
    <Layout
      title={`Judging Report — ${dancer.first_name} ${dancer.last_name} | Prix du Canada`}
      description="Individual judging report and scorecard from Prix du Canada."
    >
      <style>{`
        .report-letterhead {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 2rem;
          padding-bottom: 1.25rem;
          margin-bottom: 1.5rem;
          border-bottom: 1px solid rgba(0,0,0,0.12);
        }
        .report-letterhead-title {
          font-size: 1.5rem;
          color: var(--charcoal);
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-weight: 700;
          line-height: 1.3;
          font-family: 'Playfair Display', 'Georgia', serif;
          margin-right: 3rem;
        }
        .report-competitor-grid {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 2rem;
          align-items: center;
        }
        .report-scoreref {
          display: grid;
          grid-template-columns: auto repeat(5, 1fr);
          gap: 1rem;
          align-items: center;
        }
        .report-variation-break {
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 2px dashed rgba(0,0,0,0.12);
        }
        @media (max-width: 640px) {
          .report-letterhead {
            flex-direction: column;
            justify-content: center;
            gap: 1rem;
          }
          .report-letterhead-title {
            margin-right: 0;
            text-align: center;
            font-size: 1.05rem;
            letter-spacing: 0.15em;
          }
          .report-logo { height: 110px !important; }
          .report-competitor-grid {
            grid-template-columns: 1fr;
            gap: 1.25rem;
            text-align: center;
          }
          .report-h1 { font-size: 1.6rem !important; }
          .report-card { padding: 1.25rem 1rem !important; }
          .report-scoreref {
            grid-template-columns: repeat(5, 1fr);
            gap: 0.5rem;
          }
          .report-scoreref > div:first-child {
            grid-column: 1 / -1;
            text-align: center;
          }
        }
        /* The button promises "Save as PDF", which is only true where the print
           dialog offers a PDF destination. On a phone it generally does not —
           you get a printer picker and nothing else — so say something true
           there instead of sending people looking for an option that isn't. */
        .report-print-label-narrow { display: none; }
        .report-print-hint { display: none; }
        @media (max-width: 640px), (pointer: coarse) {
          .report-print-label-wide { display: none; }
          .report-print-label-narrow { display: inline; }
          .report-print-hint {
            display: block;
            max-width: 30rem;
            margin: 0.85rem auto 0;
            font-size: 0.8rem;
            line-height: 1.6;
            color: var(--soft-gray);
          }
        }
        @media print {
          @page { size: letter portrait; margin: 0.4in; }

          /* Printing from a phone matches BOTH this block and the 640px one
             above, because width media queries evaluate against the viewport,
             not the paper. Without these resets the printed sheet keeps the
             stacked mobile layout, so the same report looks different printed
             from a phone than from a laptop. Re-assert the wide layout: the
             page is always letter, whatever the screen is. */
          .report-letterhead {
            flex-direction: row !important;
            justify-content: space-between !important;
            gap: 2rem !important;
          }
          .report-letterhead-title {
            margin-right: 3rem !important;
            text-align: left !important;
            letter-spacing: 0.18em !important;
          }
          .report-competitor-grid {
            grid-template-columns: 1fr auto !important;
            gap: 2rem !important;
            text-align: left !important;
          }
          .report-scoreref {
            grid-template-columns: auto repeat(5, 1fr) !important;
            gap: 1rem !important;
          }
          .report-scoreref > div:first-child {
            grid-column: auto !important;
            text-align: left !important;
          }

          html, body { background: white !important; }
          .app-header, footer { display: none !important; }
          .report-no-print { display: none !important; }
          .report-page { background: white !important; padding: 0 !important; }
          .report-variation-break {
            break-before: page;
            page-break-before: always;
            margin-top: 0 !important;
            padding-top: 0 !important;
            border-top: none !important;
          }
          .report-card {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            box-shadow: none !important;
            padding: 0.55rem 0.85rem !important;
            margin-bottom: 0.4rem !important;
            border: 1px solid rgba(0,0,0,0.15) !important;
          }
          .report-header-print { padding: 0.5rem 0 0.35rem !important; margin-bottom: 0.5rem !important; }
          .report-logo { height: 100px !important; }
          .report-h1 { font-size: 1.35rem !important; margin: 0 !important; }
          .report-h2 { font-size: 0.9rem !important; }
          .report-body { font-size: 0.8rem !important; line-height: 1.45 !important; }
          .report-final-score { font-size: 2.4rem !important; }
          .report-radar-svg { max-width: 220px !important; }
          .report-scoreref-item { font-size: 0.7rem !important; }
          .report-viz-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 0.75rem !important;
            align-items: center !important;
          }
        }
      `}</style>

      <div className="report-page" style={{ background: 'white', paddingBottom: '4rem', paddingTop: '2.5rem' }}>
        <div className="container" style={{ maxWidth: '960px', padding: '0 1rem' }}>
          {blocks.map((b, i) => (
            <VariationReport
              key={b.variation.id}
              dancer={dancer}
              variation={b.variation}
              report={b.report}
              rubric={rubric}
              year={year}
              isFirst={i === 0}
            />
          ))}

          {/* Print button */}
          <div className="report-no-print" style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button
              onClick={() => window.print()}
              style={{
                background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '0.95rem',
              }}
            >
              <span className="report-print-label-wide">Print / Save as PDF</span>
              <span className="report-print-label-narrow">Print this report</span>
            </button>
            <p className="report-print-hint">
              On a phone the print sheet usually only offers a printer. To keep a copy,
              use your browser's share menu instead — on iPhone choose <strong>Options → PDF</strong>,
              on Android <strong>Print → Save as PDF</strong>. Opening this page on a computer is easier.
            </p>
            <div style={{ marginTop: '0.75rem' }}>
              <Link to="/profile" style={{ fontSize: '0.85rem', color: 'var(--soft-gray)' }}>
                Back to My Profile
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Report;
